import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { User, LoginCredentials, AuthContextType } from '../types/auth'
import { authApi, setTokenData, getTokenData, clearTokenData, refreshAccessToken, getAccessToken } from '../utils/apiClient'

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Check if user is logged in on app start
  useEffect(() => {
    const checkAuth = async () => {
      const tokenData = getTokenData()
      const savedUser = localStorage.getItem('user')
      
      if (tokenData && savedUser) {
        // Check if token needs refresh
        const expiresAt = tokenData.expires_at
        const bufferTime = 5 * 60 * 1000 // 5 minutes
        const shouldRefresh = Date.now() >= (expiresAt - bufferTime)
        
        if (shouldRefresh) {
          const refreshed = await refreshAccessToken()
          if (!refreshed) {
            // Refresh failed, clear auth
            clearTokenData()
            localStorage.removeItem('user')
            setUser(null)
            setIsLoading(false)
            return
          }
        }
        
        setUser(JSON.parse(savedUser))
      } else if (tokenData && !savedUser) {
        // Token exists but no user data, clear token
        clearTokenData()
      } else if (!tokenData && savedUser) {
        // User data exists but no token, clear user
        localStorage.removeItem('user')
      }
      
      setIsLoading(false)
    }

    checkAuth()
  }, [])

  const login = async (credentials: LoginCredentials): Promise<boolean> => {
    setIsLoading(true)
    
    try {
      // Call login API
      const response = await authApi.login(credentials.email, credentials.password)
      
      // Store token data
      setTokenData({
        access_token: response.access_token,
        refresh_token: response.refresh_token,
        expires_in: response.expires_in,
        token_type: response.token_type || 'Bearer'
      })

      // Extract user info from token (JWT payload) - simplified for now
      // In production, you might want to decode JWT or get user info from API
      const userData: User = {
        id: credentials.email, // Using email as ID temporarily
        username: credentials.email,
        email: credentials.email,
        name: credentials.email.split('@')[0] // Extract name from email
      }

      setUser(userData)
      localStorage.setItem('user', JSON.stringify(userData))
      
      setIsLoading(false)
      return true
    } catch (error) {
      console.error('Login failed:', error)
      setIsLoading(false)
      return false
    }
  }

  const logout = () => {
    setUser(null)
    localStorage.removeItem('user')
    clearTokenData()
    // Clear remember me data
    localStorage.removeItem('rememberMe')
    localStorage.removeItem('savedEmail')
    localStorage.removeItem('savedUsername')
    localStorage.removeItem('rememberedEmail')
    sessionStorage.removeItem('logoutTimestamp')
  }

  const refreshToken = async (): Promise<boolean> => {
    try {
      return await refreshAccessToken()
    } catch (error) {
      console.error('Token refresh failed:', error)
      return false
    }
  }

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user && !!getTokenData(),
    isLoading,
    login,
    logout,
    refreshToken,
    getAccessToken
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
